import React from 'react';
import { BrowserRouter as Router, Switch, Route, Redirect, RouteProps } from 'react-router-dom';
import ScrollToTop from 'hooks/useScrollTop';

import Home from 'containers/home';
import Category from 'containers/category';
import AccountPanel from 'containers/account';
import page404 from 'containers/Page/404';
import UseGlobalSync from 'hooks/useStoreSync';
import { useLoggedInStateQuery } from 'graphql/types';
import MainLayout from 'components/mainLayout';
import SingleGroup from 'containers/singleGroup';
import SingleEvent from 'containers/singleEvent';
import PostsView from 'containers/postsView';
import SinglePostView from 'containers/singlePostView';
import PublicProfile from 'containers/publicProfile';
import CalendarView from 'containers/calendarView';
import ResetPassword from 'containers/resetPassword';
import PublicPage from 'containers/publicPage';
import CommunityView from 'containers/communityView';
import Notification from 'components/notification/notification';
import ChatViewPage from 'containers/chatView';
import AccountGroupsPage from 'containers/groupsView';
import AccountEventsPage from 'containers/account/events';
import CreateGroup from 'containers/account/createUpdateGroup/createGroup';
import UpdateGroup from 'containers/account/createUpdateGroup/updateGroup';
import ManageGroups from 'containers/account/manageGroup';
import CreateEvent from 'containers/account/createUpdateEvent/createEvent';
import UpdateEvent from 'containers/account/createUpdateEvent/updateEvent';
import ManageBookings from 'containers/account/bookings';
import PersonalEventFormPage from 'containers/personalEventCreate';
import ReferenceResponsePage from 'containers/referenceResponse';
import PrivacyPolicy from 'containers/legal/privacyPolicy';
import TermsOfService from 'containers/legal/termsOfService';
import Signup from 'components/signup';
import Login from 'components/login';

interface PrivateRouteProps extends RouteProps {
  component: React.FC<any>;
  layout?: React.ComponentType<any>;
  loggedInState: boolean | false;
}

type RenderComponent = (props?: any) => React.ReactNode;
const PrivateRoute = ({ component: WrapperComponent, layout: Layout, location, loggedInState, ...rest }: PrivateRouteProps) => {
  const isAdmin = false;
  let comp: RenderComponent = () => (
    <Redirect
      to={{
        pathname: '/login',
        state: { from: location },
      }}
    />
  );
  const { path } = rest;
  const isMessagesRoute =
    path === '/messages' || path === '/messages/:conversationId' || path === '/message';
  const isPrivateGroupsEditor =
    path === '/groups/create' || path === '/groups/update/:id' || path === '/groups/manage/:id';
  const isPrivateEventsEditor =
    path === '/events/create' || path === '/events/update/:id' || path === '/events/bookings/:id';
  const isPrivateCalendarEditor =
    path === '/calendar/personal-events/create' || path === '/calendar/personal-events/edit/:id';
  if (
    (loggedInState &&
      (path === '/account' ||
        isMessagesRoute ||
        path === '/groups' ||
        isPrivateGroupsEditor ||
        path === '/events' ||
        isPrivateEventsEditor ||
        isPrivateCalendarEditor)) ||
    (isAdmin && path === '/admin')
  ) {
    comp = (props) => {
      if (Layout) {
        return (
          <Layout {...props}>
            <WrapperComponent {...props} />
          </Layout>
        );
      }
      return <WrapperComponent {...props} />;
    };
  }
  return <Route {...rest} render={comp} />;
};

interface LayoutWrapperProps extends RouteProps {
  component: React.ComponentType<any>;
  layout: React.ComponentType<any>;
}

function LayoutWrapper({ component: Component, layout: Layout, ...rest }: LayoutWrapperProps) {
  return (
    <Route
      {...rest}
      render={(props) => (
        <Layout {...props}>
          <Component {...props} />
        </Layout>
      )}
    />
  );
}

export default function RouterWrapper() {
  UseGlobalSync();
  const { data: loggedInStateData } = useLoggedInStateQuery();
  const loggedInState = loggedInStateData?.loggedInState;
  return (
    <Router>
      {loggedInState && <Notification />}
      <ScrollToTop />
      <Switch>
        <LayoutWrapper exact path="/" component={Home} layout={MainLayout} />
        <LayoutWrapper exact path="/category/:id" component={Category} layout={MainLayout} />
        <PrivateRoute exact path="/groups/create" component={CreateGroup} layout={MainLayout} loggedInState={loggedInState} />
        <PrivateRoute exact path="/groups/update/:id" component={UpdateGroup} layout={MainLayout} loggedInState={loggedInState} />
        <PrivateRoute exact path="/groups/manage/:id" component={ManageGroups} layout={MainLayout} loggedInState={loggedInState} />
        <LayoutWrapper exact path="/groups/:slug" component={SingleGroup} layout={MainLayout} />
        <PrivateRoute exact path="/events/create" component={CreateEvent} layout={MainLayout} loggedInState={loggedInState} />
        <PrivateRoute exact path="/events/update/:id" component={UpdateEvent} layout={MainLayout} loggedInState={loggedInState} />
        <PrivateRoute exact path="/events/bookings/:id" component={ManageBookings} layout={MainLayout} loggedInState={loggedInState} />
        <LayoutWrapper exact path="/events/:id" component={SingleEvent} layout={MainLayout} />
        <PrivateRoute exact path="/groups" component={AccountGroupsPage} layout={MainLayout} loggedInState={loggedInState} />
        <PrivateRoute exact path="/events" component={AccountEventsPage} layout={MainLayout} loggedInState={loggedInState} />
        <LayoutWrapper exact path="/posts" component={PostsView} layout={MainLayout} />
        <LayoutWrapper exact path="/community" component={CommunityView} layout={MainLayout} />
        <LayoutWrapper exact path="/posts/:slug" component={SinglePostView} layout={MainLayout} />
        <LayoutWrapper exact path="/profile/:id" component={PublicProfile} layout={MainLayout} />
        <LayoutWrapper exact path="/calendar" component={CalendarView} layout={MainLayout} />
        <LayoutWrapper exact path="/reference-response" component={ReferenceResponsePage} layout={MainLayout} />
        <PrivateRoute
          exact
          path="/calendar/personal-events/create"
          component={PersonalEventFormPage}
          layout={MainLayout}
          loggedInState={loggedInState}
        />
        <PrivateRoute
          exact
          path="/calendar/personal-events/edit/:id"
          component={PersonalEventFormPage}
          layout={MainLayout}
          loggedInState={loggedInState}
        />
        <LayoutWrapper exact path="/reset-password" component={ResetPassword} layout={MainLayout} />
        <LayoutWrapper exact path="/signup" component={Signup} layout={MainLayout} />
        <LayoutWrapper exact path="/login" component={Login} layout={MainLayout} />
        <LayoutWrapper exact path="/privacy" component={PrivacyPolicy} layout={MainLayout} />
        <LayoutWrapper exact path="/terms" component={TermsOfService} layout={MainLayout} />
        <LayoutWrapper exact path="/page/:slug" component={PublicPage} layout={MainLayout} />
        <Route exact path="/account/groups/create" render={() => <Redirect to="/groups/create" />} />
        <Route
          exact
          path="/account/groups/update/:id"
          render={({ match }) => <Redirect to={`/groups/update/${match.params.id}`} />}
        />
        <Route
          exact
          path="/account/groups/:id"
          render={({ match }) => <Redirect to={`/groups/manage/${match.params.id}`} />}
        />
        <Route exact path="/account/groups" render={() => <Redirect to="/groups" />} />
        <Route exact path="/account/events/create" render={() => <Redirect to="/events/create" />} />
        <Route
          exact
          path="/account/events/update/:id"
          render={({ match }) => <Redirect to={`/events/update/${match.params.id}`} />}
        />
        <Route
          exact
          path="/account/events/bookings/:id"
          render={({ match }) => <Redirect to={`/events/bookings/${match.params.id}`} />}
        />
        <Route exact path="/account/events" render={() => <Redirect to="/events" />} />
        <Route exact path="/account/messages" render={() => <Redirect to="/messages" />} />
        <PrivateRoute exact path="/message" component={ChatViewPage} layout={MainLayout} loggedInState={loggedInState} />
        <PrivateRoute
          path="/messages/:conversationId"
          component={ChatViewPage}
          layout={MainLayout}
          loggedInState={loggedInState}
        />
        <PrivateRoute exact path="/messages" component={ChatViewPage} layout={MainLayout} loggedInState={loggedInState} />
        <PrivateRoute path="/account" component={AccountPanel} layout={MainLayout} loggedInState={loggedInState} />
        <Route component={page404} />
      </Switch>
    </Router>
  );
}